import { For, Show, createMemo, type Component } from 'solid-js';
import type { PlacedBet, PocketId, WheelStyle } from '~/domain/types';
import { betKey, coveredPockets } from '~/domain/bets';
import { getWheel } from '~/domain/wheels';
import {
  betPosition,
  feltLayout,
  resolveFeltHit,
  type FeltHit,
  type FeltRegion,
} from '~/view/feltLayout';
import { polar } from '~/view/geometry';
import { layerColour } from '~/view/layers';
import type { PreviewState } from '~/view/pocketState';

/**
 * The betting board — the input surface. Chips go where they physically go on
 * a real felt; the wheel next to it shows what that does to ring coverage.
 *
 * Line bets are placed by clicking between cells. Hit resolution is the pure
 * `resolveFeltHit`, which snaps to the nearest line within a band rather than
 * demanding an exact hit (§4).
 */

const PAD = 0.28;

export interface FeltProps {
  style: WheelStyle;
  bets: readonly PlacedBet[];
  /** Transient preview, owned by the parent so the wheel shows the same thing. */
  preview?: PreviewState | null;
  /** The bet being previewed, so the ghost chip lands where the real one would. */
  previewTarget?: Pick<PlacedBet, 'betType' | 'target'> | null;
  /** Bets to emphasise — set when a wheel pocket is hovered. */
  highlightBets?: ReadonlySet<string>;
  /**
   * Chips per layer for each target, keyed by `betKey`. When a target appears
   * here its chip is drawn as layer bands instead of a single disc.
   */
  chipLayers?: ReadonlyMap<string, readonly number[]>;
  /** Hovering a layer in the composer dims every other layer's chips. */
  dimLayersExcept?: number | null;
  class?: string;
  onHoverTarget?: (hit: FeltHit | null) => void;
  onPlace?: (betType: PlacedBet['betType'], target: string) => void;
  onRemove?: (betType: PlacedBet['betType'], target: string) => void;
}

/** One slice of a layered chip's pie, in the layer's stack position and angle span. */
interface ChipWedge {
  index: number;
  startDeg: number;
  endDeg: number;
}

/**
 * Splits one target's per-layer chip counts into pie wedges, each angle
 * proportional to that layer's share of the pile so the geometry itself
 * carries no information the count label doesn't already say twice —
 * colour attributes a slice, the label (drawn once, over all of them) still
 * carries the total. A layer with zero chips contributes no wedge. Angles
 * start at twelve o'clock and run clockwise, matching the wheel's convention
 * in `~/view/geometry` so the felt and the ring never disagree about which
 * way "clockwise" points.
 */
function chipWedges(layers: readonly number[]): ChipWedge[] {
  const total = layers.reduce((sum, count) => sum + count, 0);
  if (total <= 0) return [];
  const wedges: ChipWedge[] = [];
  let angle = 0;
  layers.forEach((count, index) => {
    if (count <= 0) return;
    const span = (count / total) * 360;
    wedges.push({ index, startDeg: angle, endDeg: angle + span });
    angle += span;
  });
  return wedges;
}

/**
 * SVG path `d` for one pie wedge. Never call this with a span of exactly
 * 360° — an arc's start and end point coincide there, so it collapses to
 * nothing instead of drawing a circle. Single-layer chips are drawn as a
 * plain `<circle>` instead of a wedge for exactly this reason.
 */
function wedgePath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

const Felt: Component<FeltProps> = (props) => {
  const layout = createMemo(() => feltLayout(props.style));
  const wheel = createMemo(() => getWheel(props.style));

  const chipsByKey = createMemo(() => {
    const map = new Map<string, number>();
    for (const bet of props.bets) map.set(betKey(bet), bet.chips);
    return map;
  });

  /** Pockets under the current preview, for outlining their cells. */
  const previewPockets = createMemo(() => props.preview?.pockets ?? new Set<PocketId>());

  /** Layer wedges for a target, empty when it has no `chipLayers` entry (or an all-zero one). */
  const wedgesFor = (bet: PlacedBet): ChipWedge[] => chipWedges(props.chipLayers?.get(betKey(bet)) ?? []);

  // A hovered legend entry dims every other layer so the eye can trace just its chips.
  const dimOpacity = (index: number): number =>
    props.dimLayersExcept != null && props.dimLayersExcept !== index ? 0.25 : 1;

  const regionColour = (region: FeltRegion): string => {
    if (region.kind === 'number' || region.kind === 'zero') {
      const colour = wheel().pockets.get(region.target as PocketId)?.colour;
      if (colour === 'red') return 'var(--color-pocket-red)';
      if (colour === 'black') return 'var(--color-pocket-black)';
      return 'var(--color-pocket-green)';
    }
    return 'var(--color-surface-2)';
  };

  // -- pointer handling -----------------------------------------------------

  let svgRef: SVGSVGElement | undefined;

  /** Client coordinates to felt cell units. */
  const toCell = (event: PointerEvent | MouseEvent): { x: number; y: number } | null => {
    if (!svgRef) return null;
    const ctm = svgRef.getScreenCTM();
    if (!ctm) return null;
    const point = svgRef.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  };

  const hitAt = (event: PointerEvent | MouseEvent): FeltHit | null => {
    const cell = toCell(event);
    return cell ? resolveFeltHit(layout(), cell.x, cell.y) : null;
  };

  const targetOf = (hit: FeltHit) =>
    hit.kind === 'point'
      ? { betType: hit.point.betType, target: hit.point.target }
      : { betType: hit.region.betType, target: hit.region.target };

  const handleMove = (event: PointerEvent) => props.onHoverTarget?.(hitAt(event));

  const handleClick = (event: MouseEvent) => {
    const hit = hitAt(event);
    if (!hit) return;
    const { betType, target } = targetOf(hit);
    // Shift-click removes, matching the right-click path.
    if (event.shiftKey) props.onRemove?.(betType, target);
    else props.onPlace?.(betType, target);
  };

  const handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    const hit = hitAt(event);
    if (!hit) return;
    const { betType, target } = targetOf(hit);
    props.onRemove?.(betType, target);
  };

  const previewIsRemove = () => props.preview?.mode === 'remove';
  const previewStroke = () =>
    previewIsRemove() ? 'var(--color-remove)' : 'var(--color-preview-strong)';

  const previewPoint = createMemo(() => {
    const target = props.previewTarget;
    return target ? betPosition(layout(), target) : null;
  });

  return (
    <svg
      ref={svgRef}
      viewBox={`${layout().minX - PAD} ${layout().minY - PAD} ${layout().width + PAD * 2} ${
        layout().height + PAD * 2
      }`}
      preserveAspectRatio="xMidYMid meet"
      class={props.class ?? 'h-full w-full'}
      role="group"
      aria-label={`${props.style === 'single-zero' ? 'European' : 'American'} betting layout`}
      onPointerMove={handleMove}
      onPointerLeave={() => props.onHoverTarget?.(null)}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      style={{ cursor: 'crosshair', 'touch-action': 'manipulation' }}
    >
      {/* Boxes. */}
      <g>
        <For each={layout().regions}>
          {(region) => (
            <g>
              <rect
                x={region.x}
                y={region.y}
                width={region.w}
                height={region.h}
                rx="0.06"
                fill={regionColour(region)}
                stroke="var(--color-line-strong)"
                stroke-width="0.03"
              />
              <text
                x={region.x + region.w / 2}
                y={region.y + region.h / 2}
                text-anchor="middle"
                dominant-baseline="central"
                class="tnum"
                font-size={region.kind === 'number' || region.kind === 'zero' ? '0.38' : '0.26'}
                font-weight="600"
                fill={
                  region.kind === 'number' || region.kind === 'zero'
                    ? 'rgba(255,255,255,0.92)'
                    : 'var(--color-ink-dim)'
                }
                pointer-events="none"
              >
                {region.label}
              </text>
              {/* 6 and 9 are underlined here too, for the same reason as on the
                  wheel — and because a felt does it. */}
              <Show when={region.label === '6' || region.label === '9'}>
                <line
                  x1={region.x + region.w / 2 - 0.13}
                  y1={region.y + region.h / 2 + 0.22}
                  x2={region.x + region.w / 2 + 0.13}
                  y2={region.y + region.h / 2 + 0.22}
                  stroke="rgba(255,255,255,0.92)"
                  stroke-width="0.04"
                  pointer-events="none"
                />
              </Show>
            </g>
          )}
        </For>
      </g>

      {/* Preview: outline every cell the hovered bet would touch. */}
      <g fill="none" pointer-events="none">
        <For each={layout().regions.filter((region) => region.kind === 'number' || region.kind === 'zero')}>
          {(region) => (
            <Show when={previewPockets().has(region.target as PocketId)}>
              <rect
                x={region.x + 0.04}
                y={region.y + 0.04}
                width={region.w - 0.08}
                height={region.h - 0.08}
                rx="0.05"
                fill={previewIsRemove() ? 'var(--color-remove)' : 'var(--color-preview)'}
                fill-opacity={previewIsRemove() ? 0.4 : 0.28}
                stroke={previewStroke()}
                stroke-width="0.05"
                stroke-dasharray={previewIsRemove() ? '0.1 0.07' : undefined}
              />
            </Show>
          )}
        </For>
      </g>

      {/* Placed chips. */}
      <g pointer-events="none">
        <For each={props.bets}>
          {(bet) => {
            const at = createMemo(() => betPosition(layout(), bet));
            const emphasised = () => props.highlightBets?.has(betKey(bet)) ?? false;
            const wedges = createMemo(() => wedgesFor(bet));
            return (
              <Show when={at()}>
                {(position) => (
                  <g>
                    <Show
                      when={wedges().length > 0}
                      fallback={
                        <circle
                          cx={position().x}
                          cy={position().y}
                          r="0.3"
                          fill="var(--color-placed-deep)"
                          stroke={emphasised() ? '#ffffff' : 'var(--color-placed-strong)'}
                          stroke-width={emphasised() ? 0.07 : 0.045}
                        />
                      }
                    >
                      {/* A single contributing layer is a plain circle — a 360° arc's
                          endpoints coincide, so drawing it as a one-wedge path collapses
                          to nothing rather than a disc. */}
                      <Show
                        when={wedges().length > 1}
                        fallback={
                          <Show when={wedges()[0]}>
                            {(wedge) => (
                              <circle
                                cx={position().x}
                                cy={position().y}
                                r="0.3"
                                fill={layerColour(wedge().index).hue}
                                stroke={emphasised() ? '#ffffff' : layerColour(wedge().index).edge}
                                stroke-width={emphasised() ? 0.07 : 0.045}
                                opacity={dimOpacity(wedge().index)}
                              />
                            )}
                          </Show>
                        }
                      >
                        <For each={wedges()}>
                          {(wedge) => (
                            <path
                              d={wedgePath(position().x, position().y, 0.3, wedge.startDeg, wedge.endDeg)}
                              fill={layerColour(wedge.index).hue}
                              stroke={layerColour(wedge.index).edge}
                              stroke-width="0.02"
                              opacity={dimOpacity(wedge.index)}
                            />
                          )}
                        </For>
                      </Show>
                      {/* Opaque centre so the total reads over any hue beneath it — colour
                          carries attribution, this number still carries the quantity. */}
                      <circle cx={position().x} cy={position().y} r="0.19" fill="var(--color-surface-0)" />
                    </Show>
                    <text
                      x={position().x}
                      y={position().y}
                      text-anchor="middle"
                      dominant-baseline="central"
                      class="tnum"
                      font-size="0.28"
                      font-weight="700"
                      fill="#ffffff"
                    >
                      {chipsByKey().get(betKey(bet)) ?? bet.chips}
                    </text>
                  </g>
                )}
              </Show>
            );
          }}
        </For>
      </g>

      {/* Ghost chip showing exactly where the hovered bet would land. */}
      <Show when={previewPoint()}>
        {(position) => (
          <circle
            cx={position().x}
            cy={position().y}
            r="0.3"
            fill="none"
            stroke={previewStroke()}
            stroke-width="0.06"
            stroke-dasharray="0.12 0.08"
            pointer-events="none"
          />
        )}
      </Show>
    </svg>
  );
};

export default Felt;

/** Pockets a hit would cover — the parent needs this to build the preview. */
export function pocketsForHit(hit: FeltHit, style: WheelStyle): PocketId[] {
  const target =
    hit.kind === 'point'
      ? { betType: hit.point.betType, target: hit.point.target, chips: 1 }
      : { betType: hit.region.betType, target: hit.region.target, chips: 1 };
  return coveredPockets(target, getWheel(style));
}
